import React from "react";

interface CustomField {
    fieldName: string;
    amount: string | number;
    duration: string;
    billingLogic: string;
    refund: string;
}

interface CustomFieldsTableProps {
    customFields: CustomField[];
    selectedDurations: any[];
    handleFieldChange: (index: number, key: string, value: any) => void;
    handleRefundChange: (index: number, value: string, type: 'pricing' | 'custom') => void;
}

const CustomFieldsTable: React.FC<CustomFieldsTableProps> = ({
    customFields,
    selectedDurations,
    handleFieldChange,
    handleRefundChange
}) => {
    // Check if any field in the row has a value
    const isRowActive = (field: CustomField) => {
        return (
            (field.fieldName && field.fieldName.trim() !== "") ||
            (field.amount !== "" && field.amount !== null && field.amount !== undefined) ||
            (field.duration && field.duration !== "") ||
            (field.billingLogic && field.billingLogic !== "") ||
            (field.refund && field.refund !== "")
        );
    };

    // Check if the fieldName is used in another active row
    const isDuplicateFieldName = (name: string, index: number) => {
        if (!name || name.trim() === "") return false;
        return customFields.some((f, i) => 
            i !== index && 
            isRowActive(f) &&
            f.fieldName && 
            f.fieldName.trim().toLowerCase() === name.trim().toLowerCase()
        );
    };

    // Determine if a specific field is invalid/missing in an active row
    const isFieldInvalid = (field: CustomField, key: keyof CustomField, index: number) => {
        if (!isRowActive(field)) return false;

        if (key === 'fieldName') {
            return !field.fieldName || field.fieldName.trim() === "" || isDuplicateFieldName(field.fieldName, index);
        }
        if (key === 'amount') {
            return field.amount === "" || field.amount === null || field.amount === undefined;
        }
        if (key === 'duration') {
            return !field.duration || field.duration === "";
        }
        if (key === 'billingLogic') {
            if (field.duration === "invoiceAtFirst" || field.duration === "invoiceAtLast") return false;
            return !field.billingLogic || field.billingLogic === "";
        }
        if (key === 'refund') {
            if (field.duration === "invoiceAtFirst" || field.duration === "invoiceAtLast") return false;
            return !field.refund || field.refund === "";
        }
        return false;
    };

    const getFieldClassName = (field: CustomField, key: keyof CustomField, index: number, baseClass: string) => {
        const invalid = isFieldInvalid(field, key, index);
        // Using Bootstrap's border-danger for validation feedback
        return `${baseClass} ${invalid ? 'border border-danger' : 'border-0'} shadow-none`;
    };

    return (
        <div className="row mt-2">
            <div className="col-12">
                <span style={{ marginBottom: "15px" }}><b>Custom Fields</b></span>
                <table className="table table-bordered table-sm" style={{ tableLayout: "fixed", width: "100%" }}>
                    <thead>
                        <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                            <th style={{ width: "24%" }}>Field Name</th>
                            <th style={{ width: "16%" }}>Amount</th>
                            <th style={{ width: "24%" }}>Duration</th>
                            <th style={{ width: "18%" }}>Billing Logic</th>
                            <th style={{ width: "18%" }}>Refund logic</th>
                        </tr>
                    </thead>
                    <tbody>
                        {customFields.map((field, index) => (
                            <tr key={index}>
                                <td style={{ width: "24%" }}>
                                    <input
                                        placeholder="Enter Field Name"
                                        className={getFieldClassName(field, 'fieldName', index, 'form-control')}
                                        type="text"
                                        value={field.fieldName}
                                        onChange={(e: any) => handleFieldChange(index, 'fieldName', e.target.value)}
                                    />
                                </td>
                                <td style={{ width: "16%" }}>
                                    <input
                                        placeholder="Enter Amount"
                                        className={getFieldClassName(field, 'amount', index, 'form-control')}
                                        type="number"
                                        value={field.amount}
                                        onChange={(e: any) => handleFieldChange(index, 'amount', e.target.value)}
                                    />
                                </td>
                                <td style={{ width: "24%" }}>
                                    <select
                                        className={getFieldClassName(field, 'duration', index, 'form-select')}
                                        value={field.duration}
                                        onChange={(e: any) => handleFieldChange(index, 'duration', e.target.value)}
                                    >
                                        <option value="" disabled>Select</option>
                                        {selectedDurations && selectedDurations.map((dur: any) => (
                                            <option key={dur.value} value={dur.value}>{dur.label}</option>
                                        ))}
                                        <option value="invoiceAtFirst">One time - at first Invoice</option>
                                        <option value="invoiceAtLast">One time - at last Invoice</option>
                                    </select>
                                </td>
                                <td style={{ width: "18%" }}>
                                    <select
                                        className={getFieldClassName(field, 'billingLogic', index, 'form-select')}
                                        value={field.billingLogic}
                                        disabled={field.duration === "invoiceAtFirst" || field.duration === "invoiceAtLast"}
                                        onChange={(e: any) => handleFieldChange(index, 'billingLogic', e.target.value)}
                                        style={{cursor: (field.duration === "invoiceAtFirst" || field.duration === "invoiceAtLast") ? "not-allowed" : "pointer"}}
                                    >
                                        <option value="" disabled>Select</option>
                                        <option value="Fixed">Fixed</option>
                                        <option value="Pro Rated">Pro Rated</option>
                                    </select>
                                </td>
                                <td style={{ width: "18%" }}>
                                    <select
                                        className={getFieldClassName(field, 'refund', index, 'form-select')}
                                        value={field.refund}
                                        disabled={field.duration === "invoiceAtFirst" || field.duration === "invoiceAtLast"}
                                        onChange={(e: any) => handleRefundChange(index, e.target.value, 'custom')}
                                        style={{cursor: (field.duration === "invoiceAtFirst" || field.duration === "invoiceAtLast") ? "not-allowed" : "pointer"}}
                                    >
                                        <option value="" disabled>Select</option>
                                        <option value="Refund">Refund</option>
                                        <option value="Non Refund">Non Refund</option>
                                    </select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CustomFieldsTable;
